import React from 'react';
import { LegalPage } from '../components/legal/LegalPage';

const PrivacyPage = () => (
  <LegalPage title="Privacy Policy" lastUpdated="February 10, 2026">
    <p>
      Clayworks Spaces Technologies Pvt Ltd ("<strong>Clayworks</strong>", "we", "us", "our") operates the Clayworks
      booking and commerce platform (the "<strong>Service</strong>") via our website at <strong>book.clayworks.space</strong> and
      our native mobile apps. This policy explains what we collect, why, how we use it, and how you can control it.
    </p>

    <h2>1. Information we collect</h2>
    <ul>
      <li><strong>Account data:</strong> name, email address, phone number, role (client / sales / centre manager / admin), profile photo (optional), and password (stored as a one-way bcrypt hash).</li>
      <li><strong>Booking &amp; subscription data:</strong> selected centre, dates, time slots, billing address, GSTIN (if you provide it), credit balance, and contracts you sign with us.</li>
      <li><strong>Payment data:</strong> processed by Razorpay. We store transaction IDs, amounts, and status — we do not store card numbers, CVV, UPI PINs, or banking credentials.</li>
      <li><strong>KYC documents (Virtual Office customers only):</strong> PAN, Aadhaar, business registration certificates you upload to activate your subscription. Stored encrypted at rest.</li>
      <li><strong>Device &amp; usage data:</strong> IP address, browser/app version, pages visited, and crash diagnostics — used to keep the Service stable and prevent abuse.</li>
      <li><strong>Communications:</strong> emails we send via Resend (booking confirmations, invoices, feedback requests) and the responses you choose to reply with.</li>
    </ul>

    <h2>2. How we use your data</h2>
    <ul>
      <li>To run the Service — create accounts, process bookings, allocate credits, issue invoices, send receipts and reminders.</li>
      <li>To comply with Indian tax + corporate law — GST invoice generation via Zoho, contract record-keeping under the Companies Act.</li>
      <li>To improve the Service — anonymized analytics, A/B testing of UI flows, bug diagnostics.</li>
      <li>To prevent fraud + abuse — rate limiting, KYC verification, payment dispute resolution.</li>
      <li>To send transactional emails. We do <strong>not</strong> send marketing emails without your explicit opt-in.</li>
    </ul>

    <h2>3. Third-party services</h2>
    <ul>
      <li><strong>Razorpay</strong> — payments processor (your card / UPI details go directly to Razorpay; we never see them).</li>
      <li><strong>Resend</strong> — transactional email delivery.</li>
      <li><strong>Zoho Invoice</strong> — GST-compliant invoice generation + storage.</li>
      <li><strong>Google Firebase</strong> — push notification delivery + phone-OTP authentication (when used).</li>
      <li><strong>MongoDB Atlas</strong> — primary database, hosted in the Mumbai (ap-south-1) region.</li>
    </ul>

    <h2>4. Data retention</h2>
    <p>
      We retain booking records and invoices for <strong>8 years</strong> to satisfy GST + Companies Act audit
      requirements. You can request deletion of your personal account data (name, email, phone) at any time;
      booking + invoice ledger entries will be retained in anonymized form for legal compliance.
    </p>

    <h2>5. Your rights</h2>
    <ul>
      <li>Access — request a copy of all personal data we hold about you.</li>
      <li>Correction — update any inaccurate data via your profile, or by emailing us.</li>
      <li>Deletion — request erasure of your personal data (subject to legal retention obligations above).</li>
      <li>Portability — receive your data in a structured, machine-readable format.</li>
      <li>Withdraw consent — opt out of any optional data processing at any time.</li>
    </ul>
    <p>
      To exercise any of these rights, email <strong>support@clayworks.co.in</strong> from your registered email
      address. We respond within 30 days.
    </p>

    <h2>6. Security</h2>
    <p>
      All traffic uses HTTPS (TLS 1.3). Passwords are stored as bcrypt hashes. KYC documents are encrypted at
      rest. Payment data never touches our servers. We follow industry-standard security practices including
      least-privilege access, audit logging, and routine vulnerability scanning.
    </p>

    <h2>7. Children</h2>
    <p>
      The Service is intended for users aged 18 and above. We do not knowingly collect data from minors.
    </p>

    <h2>8. Changes to this policy</h2>
    <p>
      We may update this policy. The "Last updated" date at the top reflects the most recent revision.
      Material changes will be communicated via email + in-app banner at least 14 days before they take effect.
    </p>

    <h2>9. Contact us</h2>
    <p>
      Clayworks Spaces Technologies Pvt Ltd<br />
      Email: <strong>support@clayworks.co.in</strong><br />
      Address: Clayworks Mini Forest, 3rd Floor, Site No. 74, Mass Complex, 15th Cross Road, Sarakki Industrial
      Layout, Dollar Layout, 3rd Phase, J. P. Nagar, Bengaluru, Karnataka, India.
    </p>
  </LegalPage>
);

export default PrivacyPage;
